import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HomePage } from './components/home/HomePage';
import { ArchivesPage } from './modules/roadscope/components/archives/ArchivesPage';
import { EmailVerificationGate } from './components/auth/EmailVerificationGate';
import { WelcomeOnboarding } from './components/onboarding/WelcomeOnboarding';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { LoginForm } from './components/auth/LoginForm';
import { SubscriptionPlansPage } from './components/subscription/SubscriptionPlansPage';
import { SubscriptionFlow } from './components/subscription/SubscriptionFlow';
import { BetaRequestSubmitted } from './components/auth/BetaRequestSubmitted';
import { EnterpriseInquiry } from './components/auth/EnterpriseInquiry';
import { VerificationPage } from './components/auth/VerificationPage';
import { useAuth } from './hooks/useAuth';
import { RoadGuardErrorBoundary } from './modules/roadguard';
import { PermitProErrorBoundary } from './modules/permitpro/components/PermitProErrorBoundary';
import { CargoCareErrorBoundary, CargoCareMenu } from './modules/cargocare';
import { ELDErrorBoundary } from './modules/eld';
import RoadScopeRoutes from './modules/roadscope/routes/RoadScopeRoutes';
import { SharedMapView } from './modules/roadscope/components/survey/share/SharedMapView';
import { AdminErrorBoundary } from './modules/admin/components/AdminErrorBoundary';
import { AdminRoutes } from './modules/admin/routes/AdminRoutes';

// Lazy load module components
const RoadGuardPage = lazy(() => import('./modules/roadguard/components/RoadGuardPage'));
const PermitProPage = lazy(() => import('./modules/permitpro/components/PermitProPage'));
const Dashboard = lazy(() => import('./modules/eld/components/Dashboard'));
const TeamPanel = lazy(() => import('./modules/team/components/TeamPanel'));
const NotificationsPanel = lazy(() => import('./modules/notifications/components/NotificationsPanel'));
const SettingsPanel = lazy(() => import('./modules/settings/components/SettingsPanel'));

const AppRoutes = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D3F42] flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/plans" element={<SubscriptionPlansPage />} />
      <Route path="/signup/:plan" element={<SubscriptionFlow />} />
      <Route path="/login" element={
        user ? <Navigate to="/" state={{ from: location }} replace /> : <LoginForm />
      } />
      <Route path="/verify" element={
        !localStorage.getItem('pendingEmail') ? 
        <Navigate to="/login" replace /> : 
        <VerificationPage />
      } />
      <Route path="/beta-request-submitted" element={<BetaRequestSubmitted />} />
      <Route path="/enterprise-inquiry" element={<EnterpriseInquiry />} />
      <Route path="/shared/:shareId?" element={<SharedMapView />} />

      {/* Protected Routes */}
      <Route path="/" element={
        !user ? <Navigate to="/login" replace /> : <HomePage />
      } />

      {/* ELD Routes */}
      <Route path="/eld/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <ELDErrorBoundary>
            <Dashboard />
          </ELDErrorBoundary>
        </Suspense>
      } />
      {/* RoadScope Routes */}
      <Route 
        path="/roadscope/*" 
        element={user ? <RoadScopeRoutes /> : <Navigate to="/login" replace />}
      />

      {/* RoadGuard Routes */}
      <Route path="/roadguard/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <RoadGuardErrorBoundary>
            <RoadGuardPage />
          </RoadGuardErrorBoundary>
        </Suspense>
      } />

      {/* PermitPro Routes */}
      <Route path="/permitpro/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <PermitProErrorBoundary>
            <PermitProPage />
          </PermitProErrorBoundary>
        </Suspense>
      } />

      {/* CargoCare Routes */}
      <Route path="/cargocare/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <CargoCareErrorBoundary>
            <CargoCareMenu />
          </CargoCareErrorBoundary>
        </Suspense>
      } />

      {/* Team Routes */}
      <Route path="/team/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <TeamPanel />
        </Suspense>
      } />

      {/* Notifications Routes */}
      <Route path="/notifications" element={
        <Suspense fallback={<LoadingSpinner />}>
          <NotificationsPanel />
        </Suspense>
      } />

      {/* Admin Routes */}
      <Route path="admin/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <AdminErrorBoundary>
            {user?.isAdmin ? (
              <AdminRoutes />
            ) : (
              <Navigate to="/" replace />
            )}
          </AdminErrorBoundary>
        </Suspense>
      } />

      {/* Settings Routes */}
      <Route path="/settings/*" element={
        <Suspense fallback={<LoadingSpinner />}>
          <SettingsPanel />
        </Suspense>
      } />

      {/* Other Routes */}
      <Route path="/archives" element={<ArchivesPage />} />
      <Route path="/welcome" element={<WelcomeOnboarding />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;